class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    this.addEventListener("click", this.onResultClick.bind(this));

    // In Safari/Firefox, clicking a button or link doesn't move focus to it,
    // so a click inside the dropdown blurs the input and onFocusOut would tear
    // the dropdown down mid-click — the results visibly vanish right before
    // the next page paints. Track pointer presses so focusout can tell a
    // click-inside from a real focus loss.
    this.addEventListener("pointerdown", () => {
      this.recentPointerDown = true;
      setTimeout(() => (this.recentPointerDown = false), 400);
    });

    // If the customer comes back via the back/forward cache, the "navigating"
    // freeze below must not persist or the dropdown could never close again.
    window.addEventListener("pageshow", () => delete this.dataset.navigating);
  }

  onResultClick(event) {
    // Previously, clicking a suggestion autofilled the input and re-ran the
    // search in-place — a buggy intermediate view that left text crashing into
    // the search bar. Let the suggestion's link navigate normally instead, so
    // the customer lands directly on the collection / search results page.
    //
    // While the browser unloads, keep the dropdown on screen — closing it in
    // the focusout that accompanies the click flashed an empty search panel
    // right before the destination page painted.
    if (event.target.closest("a[href]")) this.dataset.navigating = "true";
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    // The same term is already loading or rendered — e.g. a programmatic fill
    // (suggested-term click) fires both `focus` and a debounced `input`, each
    // of which lands here. Re-running would refetch and re-render the whole
    // dropdown, a visible double flash.
    if (
      newSearchTerm.length &&
      newSearchTerm === this.searchTerm &&
      (this.hasAttribute("loading") || this.getAttribute("results") === "true")
    ) {
      return;
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove stale results so they don't show up when the dropdown opens
      // again — but only while it's closed. When it's open and the customer is
      // refining the term, keep the current results on screen until the new
      // ones swap in; blanking to a spinner in between reads as a flash.
      if (!this.isOpen) {
        this.querySelector(
          "#predictive-search-results-groups-wrapper",
        )?.remove();
        this.renderedMarkup = null;
      }
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // No query, or the customer is navigating the suggestions with the arrow
    // keys (selectOption handles that) — don't submit to the results page.
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
      return;
    }

    // Curated shortcut: searching "softer skin" jumps straight to the fabric
    // science page rather than the generic results page. Normalise whitespace
    // and case so "  Softer   Skin " still matches.
    if (this.getQuery().toLowerCase().replace(/\s+/g, " ") === "softer skin") {
      event.preventDefault();
      this.dataset.navigating = "true";
      const root =
        (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      window.location.href = `${root}pages/fabric-science`;
      return;
    }

    // If predictive search surfaced exactly one product, skip the results page
    // and take the customer straight to that product.
    const productLinks = this.querySelectorAll(
      "#predictive-search-results-products-list .predictive-search__item--tile",
    );
    if (productLinks.length === 1 && productLinks[0].href) {
      event.preventDefault();
      // Freeze the dropdown open during the unload (see onResultClick) and let
      // other submit handlers know navigation is already underway so they
      // don't assign window.location a second time.
      this.dataset.navigating = "true";
      window.location.href = productLinks[0].href;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Don't collapse the dropdown while a page navigation is in flight or
      // while the blur was caused by a click inside the dropdown itself
      // (Safari/Firefox don't focus clicked buttons/links).
      if (this.dataset.navigating === "true" || this.recentPointerDown) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      // A slow, in-flight response for a previous term must not render over
      // these fresher cached results.
      this.abortController.abort();
      this.abortController = new AbortController();
      this.pendingQueryKey = null;
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Already fetching this exact term (focus + input can both request it) —
    // let the in-flight request finish instead of restarting it.
    if (this.pendingQueryKey === queryKey) return;

    // A newer term supersedes any in-flight request; without this, responses
    // can arrive out of order and stale results flash over the current ones.
    this.abortController.abort();
    this.abortController = new AbortController();
    this.pendingQueryKey = queryKey;

    // Explicitly request every resource type so pages and blog articles (e.g.
    // the Skinwear Library, reviews) are searched too — not just products.
    // Without this, predictive search defaults to a narrower set.
    const resourceTypes = "query,product,collection,page,article";
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
        `&resources[type]=${resourceTypes}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        if (this.pendingQueryKey === queryKey) this.pendingQueryKey = null;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (this.pendingQueryKey === queryKey) this.pendingQueryKey = null;
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const token = (this.renderToken = (this.renderToken || 0) + 1);

    // The dropdown already shows this exact markup (cache hit for the same
    // term) — re-inserting it would only remount every image and flash.
    if (this.renderedMarkup === resultsMarkup) {
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
      return;
    }

    // Build the new results off-DOM and give their images a brief head start
    // to load and decode before swapping, so the dropdown goes straight from
    // the old results to fully-painted new ones instead of flashing empty
    // tiles whose images pop in one by one.
    const template = document.createElement("div");
    template.innerHTML = resultsMarkup;
    const images = Array.from(template.querySelectorAll("img"));
    images.forEach((image) => (image.loading = "eager"));
    const imagesReady = Promise.all(
      images.map((image) =>
        image.decode ? image.decode().catch(() => {}) : Promise.resolve(),
      ),
    );
    const maxWait = new Promise((resolve) => setTimeout(resolve, 400));

    Promise.race([imagesReady, maxWait]).then(() => {
      if (token !== this.renderToken) return; // superseded by a newer render
      if (!this.getQuery().length) return; // input cleared while loading

      this.renderedMarkup = resultsMarkup;
      this.predictiveSearchResults.replaceChildren(...template.childNodes);
      this.setAttribute("results", true);

      this.setLiveRegionResults();
      this.open();
    });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);

document.addEventListener("DOMContentLoaded", () => {
  const searchModelOverlayInsideTheMobileMenu = document.querySelector(
    ".mfull-page .modal-overlay",
  );
  if (!searchModelOverlayInsideTheMobileMenu) {
    return;
  }
  searchModelOverlayInsideTheMobileMenu.addEventListener("click", () => {
    searchModelOverlayInsideTheMobileMenu
      .closest("details")
      ?.removeAttribute("open");
  });
});
